import axios from "axios";
import { store } from "../store";
import dompurify from "dompurify";
import {showToast} from "@/utility";

const baseUrl = window.drupalSettings?.baseUrl;

axios.interceptors.request.use(
  function (config) {
    // Aggiungi il token CSRF alla richiesta
    const csrfToken = window.drupalSettings.csrf_token;
    if (csrfToken) {
      config.headers['X-CSRF-TOKEN'] = csrfToken;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Funzione per ottenere un nuovo token CSRF
const refreshCsrfToken = async () => {
  try {
    const response = await axios.get(`${baseUrl}/pa-api/refresh-csrf-token`, {});
    // Salva il nuovo token CSRF
    window.drupalSettings.csrf_token = response.data.token;

    return response.data.token;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
}

const getRefererParams = async () => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/referer-params`, {});
    return res.data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const citizenEvent = async (payload) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/citizen-event`,
      payload
    );
    return res.data.data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const getOffices = async (
  topicId = null
) => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/offices`, {
      params: {
        topicId: topicId
      }
    });
    return res.data.data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const getTopics = async (
  officeId = null
) => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/topics`, {
      params: {
        officeId: officeId
      }
    });
    return res.data.data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const searchResources = async ({
  name,
  officeId,
  topicId,
  pageNumber = 0,
  pageSize = 10,
} = {}) => {
  try {
    const body = {
      filterOnlyVisibleResources: true,
      showDescription: true,
      status: "A",
      ...(name ? { name } : {}),
      ...(officeId ? { officeListIdEncoded: [officeId] } : {}),
      ...(topicId ? { topicListIdEncoded: [topicId] } : {}),
    };
    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${baseUrl}/pa-api/search-resources/${pageNumber}/${pageSize}`,
      data: body,
    };

    const res = await axios.request(config);
    return res.data.data;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const getResource = async (resourceId) => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/resource/${resourceId}`);
    return res.data.data;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const getReservationDetailById = async (reservationId) => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/get-reservation-detail-by-id/${reservationId}`);
    return res.data.data;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const getMonths = async (
   resourceId,
   payload
   ) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/resource-months/${resourceId}`,
      payload
    );
    return res.data.data;

  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const getDaySlots = async ({
  resourceId,
  startDate,
  endDate,
  erogationMode,
  reservationId,
  serviceSlotNumber,
} = {}) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/resource-slots/${resourceId}`,
      {startDate, endDate, erogationMode, reservationId, serviceSlotNumber }
    );

    const currentDate = new Date();
    currentDate.setHours(0, 0, 1);
    return res.data.data?.filter(daySlot => {
      const daySlotDate = new Date(daySlot.date);
      daySlotDate.setHours(23, 59, 59);
      return daySlotDate >= currentDate;
    });

  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const getServices = async (resourceId) => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/resource-services/${resourceId}`);
    return res.data.data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const lockReservation = async ({
  erogationMode,
  reservationNumber,
  reservationSlots,
  resourceId,
  serviceId,
} = {}) => {
  try {
    const res = await axios.post(`${baseUrl}/pa-api/lock-reservation/true`, {
      erogationMode,
      reservationNumber,
      reservationSlots,
      resourceId,
      serviceId,
    });
    const data = res.data.data;
    store.commit("setReservationId", data);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const updateSlots = async ({
  erogationMode,
  reservationNumber,
  reservationSlots,
  resourceId,
  serviceId,
  reservationId,
} = {}) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/update-slots/${reservationId}/true`,
      {
        erogationMode,
        reservationNumber,
        reservationSlots,
        resourceId,
        serviceId,
      }
    );
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const createReservation = async (payload, isLoggedIn) => {
  try {
    const action = !isLoggedIn ? "pa-api/create-reservation" : "pa-api/create-reservation/true";
    const res = await axios.post(
      `${baseUrl}/${action}`,
      payload
    );
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    showToast("Errore sconosciuto");
    throw new Error("Errore sconosciuto");
  }
};

const startPayment = async (reservationId, payload) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/start-payment/${reservationId}`,
      payload
    );
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const checkPayment = async (payload) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/check-payment/true`,
      payload
    );
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const exportReservationDetail = (reservationId) => {
  try {
    return window.open(
      `${baseUrl}/pa-api/export-reservation-detail/${reservationId}`,
      "_blank"
    );
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const updateReservation = async (payload, isLoggedIn = false, sendMail = true) => {
  payload.sendMail = sendMail;

  try {
    const action = !isLoggedIn ? "pa-api/update-data" : "pa-api/update-data/true";
    const res = await axios.post(`${baseUrl}/${action}`, payload);
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    showToast("Errore sconosciuto");
    throw new Error("Errore sconosciuto");
  }
};

const deleteReservation = async (reservationId) => {
  try {
    const res = await axios.post(
      `${baseUrl}/pa-api/cancel-reservation/${reservationId}`
    );
    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const getLoggedStatus = async () => {
  try {
    const res = await axios.get(`${baseUrl}/pa-api/logged-user`);
    const data = res.data.user;
    data.firstName = data.firstName[0]?.value;
    data.lastName = data.lastName[0]?.value;
    data.fiscalCode = data.fiscalCode[0]?.value;
    data.email = data.email[0]?.value;
    data.mobilePhone = data.mobilePhone[0]?.value;
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const validateIdentifyValues = async (resourceId, serviceId, payload, reservationId = null) => {

  // Aggiorno CSFR
  await refreshCsrfToken();

  try {
    let url = `${baseUrl}/pa-api/validate-identify/${resourceId}/${serviceId}/`;

    if (reservationId) {
      url += `${reservationId}`;
    }

    const res = await axios.post(url, payload);

    const data = res.data;
    return data;
  } catch (error) {
    console.error(error);
    throw new Error("Errore sconosciuto");
  }
};

const createDescriptionMarkup = (description) => {
  if (!description) return "";
  return dompurify.sanitize(description);
};

const formatDate = (date) => {
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - offset * 60 * 1000);
  return date.toISOString().split("T")[0];
};

const translateDate = (dataString) => {
  const dayWeek = [
    "Domenica",
    "Lunedì",
    "Martedì",
    "Mercoledì",
    "Giovedì",
    "Venerdì",
    "Sabato",
  ];
  const monthYear = [
    "Gennaio",
    "Febbraio",
    "Marzo",
    "Aprile",
    "Maggio",
    "Giugno",
    "Luglio",
    "Agosto",
    "Settembre",
    "Ottobre",
    "Novembre",
    "Dicembre",
  ];

  const date = new Date(dataString);
  const day = dayWeek[date.getDay()];
  const dayN = date.getDate();
  const month = monthYear[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${dayN} ${month} ${year}`;
};

const getMonthsLabelList = () => {
  const year = new Date().getFullYear();
  const monthList = [...Array(12).keys()];
  const formatter = new Intl.DateTimeFormat("it", {
    month: "long",
  });

  const getMonthName = (monthIndex) =>
    formatter.format(new Date(year, monthIndex));

  return monthList
    .map(getMonthName)
    .map((month) => month.charAt(0).toUpperCase() + month.slice(1));
};

async function updateQueryParams(params) {
  if ("URLSearchParams" in window) {
    const searchParams = new URLSearchParams(window.location.search);
    const existings = [];
    const toDelete = [];
    searchParams.forEach((value, key) => {
      params.find(
        ({ key: searchKey, value: valueKey }) =>
          key === searchKey && value === valueKey
      )
        ? existings.push(key)
        : toDelete.push(key);
    });
    toDelete.forEach((key) => searchParams.delete(key));
    params.forEach(({ value, key }) => {
      if (!existings.includes(key)) searchParams.set(key, value);
    });
    const newRelativePathQuery =
      window.location.pathname + "?" + searchParams.toString();
    history.pushState(null, "", newRelativePathQuery);
  }
}

function getMonthsArray(start, end) {
  const arr = [];
  const today = new Date();
  const dt = new Date(start);
  dt.setDate(1);

  if (dt < today) {
    dt.setFullYear(today.getFullYear());
    dt.setMonth(today.getMonth());
  }

  for (; dt <= end; dt.setMonth(dt.getMonth() + 1)) {
    arr.push(new Date(dt));
  }

  return arr;
}

function getMonthsObjectArray(months) {
  const arr = [];

  for (let i = 0; i < months.length; i++) {
    arr.push(
      {
        date: new Date(months[i].year, months[i].month -1, 1),
        status: months[i].status
      }
    );
  }

  return arr;
}

export {
  baseUrl,
  citizenEvent,
  getOffices,
  getRefererParams,
  getTopics,
  searchResources,
  getResource,
  getMonths,
  getDaySlots,
  getServices,
  lockReservation,
  updateSlots,
  getLoggedStatus,
  createDescriptionMarkup,
  createReservation,
  updateReservation,
  formatDate,
  translateDate,
  getMonthsLabelList,
  updateQueryParams,
  getMonthsArray,
  getReservationDetailById,
  exportReservationDetail,
  startPayment,
  checkPayment,
  deleteReservation,
  getMonthsObjectArray,
  validateIdentifyValues
};
